import { BoardLayout } from "@/components/board/BoardLayout";
import { ErrorBanner } from "@/components/board/ErrorBanner";
import { LoadingState } from "@/components/board/LoadingState";
import { PillSearchBar } from "@/components/common/PillSearchBar";
import { useOffices } from "@/contexts/OfficesContext";
import { useAuditLogData } from "@/hooks/useAuditLogData";
import {
  condenseSameRecord,
  toRolledUpEntries,
  type AuditOperation,
  type FieldChange,
  type RolledUpActor,
  type RolledUpEntry,
} from "@/utils/auditRollup";
import { stashMakersEmployeeFilter } from "@/utils/makersDeepLink";
import { formatMakerDisplayName } from "@/utils/nameUtils";
import { Link } from "@tanstack/react-router";
import { ArrowUpRight, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

const PAGE_SIZE = 25;

// Friendly labels for the tables we audit.
const TABLE_LABELS: Record<string, string> = {
  projects: "Project",
  project_assignments: "Assignment",
  employees: "Maker",
  employee_ooo: "OOO",
  offices: "Office",
  action_items: "ToDo",
};

const OPERATION_LABELS: Record<AuditOperation, string> = {
  INSERT: "Created",
  UPDATE: "Updated",
  DELETE: "Deleted",
};

// "Type of change" filter: which part of the app a change belongs to, keyed by the
// audited table(s). Assignments live on the Projects board, so they fold into Projects.
const CHANGE_TYPES = [
  { value: "todo", label: "To Do List", tables: ["action_items"] },
  { value: "projects", label: "Projects", tables: ["projects", "project_assignments"] },
  { value: "makers", label: "Makers", tables: ["employees", "employee_ooo"] },
] as const;

function matchesChangeType(entry: RolledUpEntry, type: string): boolean {
  if (!type) return true;
  const def = CHANGE_TYPES.find((t) => t.value === type);
  return def ? (def.tables as readonly string[]).includes(entry.table_name) : true;
}

function humanizeField(field: string): string {
  return field
    .split("_")
    .map((w) => (w.length ? w[0]!.toUpperCase() + w.slice(1) : w))
    .join(" ");
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (Array.isArray(value)) return value.length ? value.join(", ") : "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

// Technical columns that carry no meaning for a human reading the log: the row's
// own primary key and any foreign key (the related record is already named in the
// entry title), plus presentation-only bookkeeping like sort order. Matches `id`,
// any `*_id` field, and `sort_order`.
function isTechnicalField(field: string): boolean {
  return field === "id" || field === "sort_order" || field.endsWith("_id");
}

// Mirrors the values formatValue() renders as an em dash.
function isEmptyValue(value: unknown): boolean {
  return (
    value === null ||
    value === undefined ||
    value === "" ||
    (Array.isArray(value) && value.length === 0)
  );
}

// Added/removed members between two list values (order-insensitive).
function arrayDelta(
  oldArr: unknown[],
  newArr: unknown[],
): { added: string[]; removed: string[] } {
  const oldS = oldArr.map(String);
  const newS = newArr.map(String);
  return {
    added: newS.filter((v) => !oldS.includes(v)),
    removed: oldS.filter((v) => !newS.includes(v)),
  };
}

function matchesSearch(entry: RolledUpEntry, terms: string[]): boolean {
  if (terms.length === 0) return true;
  const haystack = [
    actorName(entry.actor),
    entry.actor.email,
    entry.record_label,
    TABLE_LABELS[entry.table_name] ?? entry.table_name,
    OPERATION_LABELS[entry.operation],
    ...Object.keys(entry.changes).map(humanizeField),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return terms.every((t) => haystack.includes(t.toLowerCase()));
}

// Tables whose audit title (record_label) already names the record fully, so a
// field dump on creation/deletion adds nothing. e.g. an assignment's title is
// "Project · People · Role" — the remaining columns are redundant or defaults.
const SELF_DESCRIBING_TABLES = new Set(["project_assignments"]);

// The change rows actually shown for an entry: drops synthetic keys (id / foreign
// keys) and, on a create/delete, fields that were never set (an empty value on an
// UPDATE is meaningful — "was X → blank" — so it's kept). On a self-describing
// create/delete the title says it all, so nothing is shown. Shared so the group
// header's change count matches what's rendered.
function visibleChangeFields(
  table: string,
  operation: AuditOperation,
  changes: Record<string, FieldChange>,
): [string, FieldChange][] {
  if (operation !== "UPDATE" && SELF_DESCRIBING_TABLES.has(table)) return [];
  return Object.entries(changes).filter(([field, change]) => {
    if (isTechnicalField(field)) return false;
    if (operation !== "UPDATE") {
      const value = operation === "DELETE" ? change.old : change.new;
      if (isEmptyValue(value)) return false;
      // A false boolean on a create/delete is an untouched default (an unchecked
      // box like Completed/Archived), not something the user did — hide it too.
      if (value === false) return false;
    }
    return true;
  });
}

function ChangeList({
  table,
  operation,
  changes,
}: {
  table: string;
  operation: AuditOperation;
  changes: Record<string, FieldChange>;
}) {
  const fields = visibleChangeFields(table, operation, changes);
  if (fields.length === 0) return null;

  return (
    <ul className="audit-entry__changes">
      {fields.map(([field, change]) => {
        // Nested map (e.g. weekly hours by date range): render sub-rows.
        if (
          operation === "UPDATE" &&
          isPlainObject(change.old) &&
          isPlainObject(change.new)
        ) {
          const subKeys = Array.from(
            new Set([...Object.keys(change.old), ...Object.keys(change.new)]),
          );
          return (
            <li key={field} className="audit-entry__change audit-entry__change--nested">
              <span className="audit-entry__field">{humanizeField(field)}</span>
              <ul className="audit-entry__subchanges">
                {subKeys.map((sub) => (
                  <li key={sub} className="audit-entry__subchange">
                    <span className="audit-entry__subfield">{sub}</span>
                    <span className="audit-entry__diff">
                      <span className="audit-entry__old">
                        {formatValue((change.old as Record<string, unknown>)[sub])}
                      </span>
                      <span className="audit-entry__arrow">→</span>
                      <span className="audit-entry__new">
                        {formatValue((change.new as Record<string, unknown>)[sub])}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </li>
          );
        }

        // List field (e.g. assigned employees, roles): show only what was added
        // or removed, not the whole before/after — striking out members who are
        // still present is misleading.
        if (
          operation === "UPDATE" &&
          Array.isArray(change.old) &&
          Array.isArray(change.new)
        ) {
          const { added, removed } = arrayDelta(change.old, change.new);
          return (
            <li key={field} className="audit-entry__change">
              <span className="audit-entry__field">{humanizeField(field)}</span>
              <span className="audit-entry__diff">
                {added.length > 0 && (
                  <span className="audit-entry__added">+ {added.join(", ")}</span>
                )}
                {removed.length > 0 && (
                  <span className="audit-entry__removed">− {removed.join(", ")}</span>
                )}
                {added.length === 0 && removed.length === 0 && (
                  <span className="audit-entry__new">{formatValue(change.new)}</span>
                )}
              </span>
            </li>
          );
        }

        return (
          <li key={field} className="audit-entry__change">
            <span className="audit-entry__field">{humanizeField(field)}</span>
            {operation === "UPDATE" ? (
              <span className="audit-entry__diff">
                <span className="audit-entry__old">{formatValue(change.old)}</span>
                <span className="audit-entry__arrow">→</span>
                <span className="audit-entry__new">{formatValue(change.new)}</span>
              </span>
            ) : (
              <span className="audit-entry__diff">
                {/* A delete shows the value that was removed — render it red, not
                    green, so removals read as removals (e.g. OOO "20h"). */}
                <span className={operation === "DELETE" ? "audit-entry__removed" : "audit-entry__new"}>
                  {formatValue(operation === "DELETE" ? change.old : change.new)}
                </span>
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

// Names come from BambooHR as "Last, First"; show them as "First Last".
function actorName(actor: RolledUpActor): string {
  if (actor.name) return formatMakerDisplayName(actor.name) || actor.name;
  return actor.email ?? "Unknown user";
}

function actorLabel(entry: RolledUpEntry): string {
  return actorName(entry.actor);
}

// The project an entry concerns: project rows use the label directly; assignment
// labels are "Project · Person · Role", so take the segment before the first "·".
function entryProjectName(entry: RolledUpEntry): string | null {
  if (!entry.record_label) return null;
  if (entry.table_name === "projects") return entry.record_label;
  if (entry.table_name === "project_assignments") {
    return entry.record_label.split(" · ")[0]?.trim() || null;
  }
  return null;
}

// The thing a change is "about" for grouping: the project for project-scoped rows,
// otherwise the record's own label (employee name, ...). Entries sharing an actor +
// subject within a short window collapse into one group card. Action items return
// null so they group per item (by record id) and render their own task header.
function entrySubject(entry: RolledUpEntry): string | null {
  if (entry.table_name === "action_items") return null;
  return entryProjectName(entry) ?? entry.record_label;
}

// Bold the subject in the group header, but keep a leading article ("the …")
// in normal weight so generic subjects read naturally.
function renderSubject(subject: string) {
  if (subject.startsWith("the ")) {
    return (
      <>
        the <strong>{subject.slice(4)}</strong>
      </>
    );
  }
  return <strong>{subject}</strong>;
}

// Operation verbs for this grouped view. "Added/Removed" reads naturally for the
// dominant case (roles added to / removed from a project); kept for all tables
// for a consistent vocabulary in the summary chips and badges.
const OP_VERB: Record<AuditOperation, string> = {
  INSERT: "Added",
  UPDATE: "Updated",
  DELETE: "Removed",
};

// Noun forms used for the summary count chips, singular/plural by count
// ("1 deletion", "2 deletions", ...).
const OP_NOUN: Record<AuditOperation, { one: string; other: string }> = {
  INSERT: { one: "addition", other: "additions" },
  UPDATE: { one: "update", other: "updates" },
  DELETE: { one: "deletion", other: "deletions" },
};

function opNoun(op: AuditOperation, count: number): string {
  return count === 1 ? OP_NOUN[op].one : OP_NOUN[op].other;
}

function formatTimeOnly(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
}

// Group header time: date once, then a time range (or single time if instantaneous).
function formatGroupRange(firstAt: string, lastAt: string): string {
  const date = new Date(lastAt).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const t1 = formatTimeOnly(firstAt);
  const t2 = formatTimeOnly(lastAt);
  return t1 === t2 ? `${date} · ${t2}` : `${date} · ${t1} – ${t2}`;
}

// The per-entry headline shown inside a group, with the subject (already in the
// group header) stripped off. For assignments: the person ("Abby Peterson") with
// the role as a muted suffix, or "Developer added" for a newly created empty role.
function entryHeadline(entry: RolledUpEntry): { lead: string; role: string | null } {
  if (entry.table_name === "project_assignments" && entry.record_label) {
    const parts = entry.record_label.split(" · ").map((s) => s.trim());
    const who = parts[1] ?? "";
    const role = parts[2] || null;
    const unassigned = !who || who.toLowerCase() === "unassigned";
    if (entry.operation === "INSERT" && unassigned && role) {
      return { lead: `${role} added`, role: null };
    }
    if (!unassigned) return { lead: who, role };
    return { lead: role ?? "Assignment", role: null };
  }
  // To-do entries live under a "the To Do List" group header, so a per-row label
  // is just noise — leave it blank and let the operation badge carry the verb.
  if (entry.table_name === "action_items") {
    return { lead: "", role: null };
  }
  return { lead: TABLE_LABELS[entry.table_name] ?? entry.table_name, role: null };
}

export interface AuditGroup {
  key: string;
  actor: RolledUpActor;
  subject: string | null;
  entries: RolledUpEntry[]; // newest-first
  counts: Record<AuditOperation, number>;
  firstAt: string;
  lastAt: string;
}

// Field display order for folded to-do entries: matches the To Do List table
// columns (see action-items.tsx). Fields not listed fall back to first-seen order.
const ACTION_ITEM_FIELD_ORDER = [
  "date",
  "client",
  "task",
  "owner",
  "due_date",
  "completed",
  "notes",
];

// Second-level rollup into one card per (actor, subject, calendar day). Every
// change one person makes to one subject — a project, a maker, or a to-do item —
// on a single day collapses into one card, no matter the gaps between edits.
// Input is newest-first; a change on a different day, or to a different subject,
// starts a new group.
function groupEntries(entries: RolledUpEntry[]): AuditGroup[] {
  const open = new Map<string, AuditGroup>();
  const result: AuditGroup[] = [];
  const keyOf = (a: RolledUpActor) => a.id ?? a.email ?? a.name ?? "unknown";

  for (const e of entries) {
    const subject = entrySubject(e);
    const isTodo = e.table_name === "action_items";
    // To-do items have no subject, so they group per item (record_id); everything
    // else groups by its subject. Both are scoped to the calendar day.
    const dayKey = localDateKey(e.lastAt);
    const k = isTodo
      ? `${keyOf(e.actor)}::todo:${e.record_id}::${dayKey}`
      : `${keyOf(e.actor)}::${subject ?? `record:${e.record_id}`}::${dayKey}`;
    const g = open.get(k);
    if (g) {
      g.entries.push(e);
      g.counts[e.operation] += 1;
      g.firstAt = e.firstAt; // e is older than everything already in g
    } else {
      const ng: AuditGroup = {
        key: `${k}::${e.ids[0]}`,
        actor: e.actor,
        subject,
        entries: [e],
        counts: { INSERT: 0, UPDATE: 0, DELETE: 0 },
        firstAt: e.firstAt,
        lastAt: e.lastAt,
      };
      ng.counts[e.operation] += 1;
      open.set(k, ng);
      result.push(ng);
    }
  }

  // Fold a to-do item's separate per-field edits into one entry, then recount the
  // per-operation totals used by the (non-to-do) summary chips.
  for (const g of result) {
    g.entries = condenseSameRecord(g.entries, "action_items", ACTION_ITEM_FIELD_ORDER);
    g.counts = { INSERT: 0, UPDATE: 0, DELETE: 0 };
    for (const e of g.entries) g.counts[e.operation] += 1;
  }
  return result;
}

// Action-item groups are keyed per record, so every entry shares the table.
function isTodoGroup(group: AuditGroup): boolean {
  return group.entries[0]?.table_name === "action_items";
}

// The task an action-item group is about. The label is captured before each edit,
// so on a brand-new item it's still blank even though the task was filled in the
// same burst — prefer the latest `task` value actually recorded, then fall back to
// the label, then a placeholder when there's genuinely no task text.
function todoTaskName(group: AuditGroup): string {
  for (const e of group.entries) {
    const next = e.changes.task?.new;
    if (typeof next === "string" && next.trim()) return next.trim();
    if (e.record_label?.trim()) return e.record_label.trim();
  }
  return "[empty name]";
}

// How many change rows the group represents (matches what's shown when expanded),
// summed across its entries.
function groupChangeCount(group: AuditGroup): number {
  return group.entries.reduce(
    (sum, e) => sum + visibleChangeFields(e.table_name, e.operation, e.changes).length,
    0,
  );
}

// A link from a group to the thing it changed: the project (expanded) on the
// board, the maker filtered into the makers table, or the task in the to do list.
// `onClick` runs the same sessionStorage handoff the in-app maker links use, so
// the destination opens pre-filtered. Returns null for tables we can't deep-link
// (e.g. offices, which have no dedicated view).
interface GroupLink {
  // Restricted to the app's known routes so the value type-checks against
  // TanStack Router's `<Link to>`. Search params are passed structured and the
  // router handles URL encoding.
  to: "/" | "/makers" | "/action-items";
  search?: Record<string, string>;
  title: string;
  onClick?: () => void;
}

function groupDeepLink(group: AuditGroup): GroupLink | null {
  const first = group.entries[0];
  if (!first) return null;

  if (first.table_name === "action_items") {
    return {
      to: "/action-items",
      search: { item: first.record_id },
      title: "Open in the to do list",
    };
  }

  if (first.table_name === "employees") {
    const employeeId = first.record_id;
    return {
      to: "/makers",
      title: "View this maker",
      onClick: () => stashMakersEmployeeFilter([employeeId]),
    };
  }

  if (first.table_name === "projects" || first.table_name === "project_assignments") {
    // A project's own audit row carries the project id directly; an assignment
    // row only carries the assignment id, so fall back to the project name (the
    // board resolves it to the project and expands it).
    const projectEntry = group.entries.find((e) => e.table_name === "projects");
    if (projectEntry) {
      return { to: "/", search: { project: projectEntry.record_id }, title: "View this project" };
    }
    if (group.subject) {
      return { to: "/", search: { projectName: group.subject }, title: "View this project" };
    }
  }

  return null;
}

function localDateKey(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function todayDateKey(): string {
  return localDateKey(new Date().toString());
}

// Mirrors the office-filter convention used across the board pages: empty or
// ["all"] means "no office filter". Entries with no office (office_id === null,
// e.g. action_items) are always shown.
function matchesOffice(entry: RolledUpEntry, selectedOffices: string[]): boolean {
  if (selectedOffices.length === 0 || selectedOffices[0] === "all") return true;
  if (entry.office_id == null) return true;
  return selectedOffices.includes(entry.office_id);
}

// The audit log body without the page chrome, so it can render both as the
// standalone /audit-log route and as a tab inside the Settings page.
export function AuditLogView() {
  const { entries, loading, error } = useAuditLogData();
  const { selectedOffices } = useOffices();
  const [searchPills, setSearchPills] = useState<string[]>([]);
  const [searchInput, setSearchInput] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [projectFilter, setProjectFilter] = useState("");
  const [actorFilter, setActorFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");

  const rolled = useMemo(() => toRolledUpEntries(entries), [entries]);

  const activeSearchTerms = useMemo(
    () => [...searchPills, ...(searchInput.trim() ? [searchInput.trim()] : [])],
    [searchPills, searchInput],
  );

  // Filter option lists derived from all entries (stable regardless of filters).
  const projectOptions = useMemo(() => {
    const set = new Set<string>();
    for (const e of rolled) {
      const name = entryProjectName(e);
      if (name) set.add(name);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [rolled]);

  const actorOptions = useMemo(() => {
    const set = new Set<string>();
    for (const e of rolled) set.add(actorLabel(e));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [rolled]);

  const visible = useMemo(
    () =>
      rolled.filter((e) => {
        if (!matchesOffice(e, selectedOffices)) return false;
        if (!matchesSearch(e, activeSearchTerms)) return false;
        if (!matchesChangeType(e, typeFilter)) return false;
        // The project dropdown only applies while the Projects type is selected.
        if (typeFilter === "projects" && projectFilter && entryProjectName(e) !== projectFilter) return false;
        if (actorFilter && actorLabel(e) !== actorFilter) return false;
        if (dateFrom || dateTo) {
          const key = localDateKey(e.lastAt);
          if (dateFrom && key < dateFrom) return false;
          if (dateTo && key > dateTo) return false;
        }
        return true;
      }),
    [rolled, selectedOffices, activeSearchTerms, typeFilter, projectFilter, actorFilter, dateFrom, dateTo],
  );

  // Fold the filtered entries into one card per (actor, subject) burst.
  const groups = useMemo(() => groupEntries(visible), [visible]);

  // Groups are collapsed by default; track which ones the user has expanded.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggleExpanded = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const hasActiveFilters = Boolean(
    dateFrom || dateTo || projectFilter || actorFilter || typeFilter ||
      searchPills.length || searchInput,
  );

  const clearFilters = () => {
    setDateFrom("");
    setDateTo("");
    setProjectFilter("");
    setActorFilter("");
    setTypeFilter("");
    setSearchPills([]);
    setSearchInput("");
  };

  const setToday = () => {
    const today = todayDateKey();
    setDateFrom(today);
    setDateTo(today);
  };

  const [page, setPage] = useState(1);
  const listRef = useRef<HTMLDivElement>(null);

  const totalPages = Math.max(1, Math.ceil(groups.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);

  // Reset to the first page whenever the filters / result set change.
  useEffect(() => {
    setPage(1);
  }, [activeSearchTerms, dateFrom, dateTo, projectFilter, actorFilter, typeFilter, selectedOffices, groups.length]);

  // Scroll back to the top when paging.
  useEffect(() => {
    listRef.current?.scrollTo({ top: 0 });
  }, [currentPage]);

  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const pageGroups = groups.slice(startIndex, startIndex + PAGE_SIZE);

  return (
    <>
      <div className="board-controls">
        <div className="audit-log__filters">
          <div className="audit-log__search">
            <PillSearchBar
              pills={searchPills}
              onPillsChange={setSearchPills}
              inputValue={searchInput}
              onInputChange={setSearchInput}
              placeholder="Search audit log (user, project, field)..."
            />
          </div>
          <button
            type="button"
            className="secondary-button small-button audit-log__today"
            onClick={setToday}
          >
            Set to Today
          </button>
          <label className="audit-log__filter">
            <span>From</span>
            <input
              type="date"
              value={dateFrom}
              max={dateTo || undefined}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </label>
          <label className="audit-log__filter">
            <span>To</span>
            <input
              type="date"
              value={dateTo}
              min={dateFrom || undefined}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </label>
          <label className="audit-log__filter">
            <span>Type</span>
            <select
              value={typeFilter}
              onChange={(e) => {
                const value = e.target.value;
                setTypeFilter(value);
                // Drop a stale project selection when leaving the Projects type.
                if (value !== "projects") setProjectFilter("");
              }}
            >
              <option value="">All types</option>
              {CHANGE_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </label>
          {typeFilter === "projects" && (
            <label className="audit-log__filter">
              <span>Project</span>
              <select value={projectFilter} onChange={(e) => setProjectFilter(e.target.value)}>
                <option value="">All projects</option>
                {projectOptions.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className="audit-log__filter">
            <span>Updated by</span>
            <select value={actorFilter} onChange={(e) => setActorFilter(e.target.value)}>
              <option value="">Anyone</option>
              {actorOptions.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="danger-button small-button audit-log__clear"
            onClick={clearFilters}
            disabled={!hasActiveFilters}
          >
            Clear filters
          </button>
        </div>
      </div>

      {error && <ErrorBanner message={error} entityName="audit log" />}

      {loading ? (
        <LoadingState message="Loading audit log..." />
      ) : visible.length === 0 ? (
        <div className="board-content audit-log__empty">No changes recorded yet.</div>
      ) : (
        <div className="board-content audit-log" ref={listRef}>
          {pageGroups.map((group) => {
            const isExpanded = expanded.has(group.key);
            const isTodo = isTodoGroup(group);
            const total = isTodo ? groupChangeCount(group) : group.entries.length;
            const link = groupDeepLink(group);
            return (
              <div key={group.key} className="audit-group">
                <div
                  className="audit-group__header"
                  role="button"
                  tabIndex={0}
                  aria-expanded={isExpanded}
                  onClick={() => toggleExpanded(group.key)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      toggleExpanded(group.key);
                    }
                  }}
                >
                  <div className="audit-group__heading">
                    <span className="audit-group__title">
                      <strong>{actorName(group.actor)}</strong> made {total} change
                      {total === 1 ? "" : "s"}
                      {isTodo ? (
                        <>
                          {" "}
                          to the <strong>{todoTaskName(group)}</strong> task in the
                          to do list
                        </>
                      ) : group.subject ? (
                        <>
                          {" "}
                          to {renderSubject(group.subject)}
                        </>
                      ) : null}
                      {link && (
                        <Link
                          className="audit-group__open"
                          to={link.to}
                          search={link.search}
                          title={link.title}
                          aria-label={link.title}
                          onClick={(e) => {
                            e.stopPropagation();
                            link.onClick?.();
                          }}
                        >
                          <ArrowUpRight size={14} />
                        </Link>
                      )}
                    </span>
                    <span className="audit-group__sub">
                      <span className="audit-group__range">
                        {formatGroupRange(group.firstAt, group.lastAt)}
                      </span>
                      {!isTodo &&
                        (["UPDATE", "INSERT", "DELETE"] as AuditOperation[])
                          .filter((op) => group.counts[op] > 0)
                          .map((op) => (
                            <span
                              key={op}
                              className={`audit-group__chip audit-group__chip--${op.toLowerCase()}`}
                            >
                              {group.counts[op]} {opNoun(op, group.counts[op])}
                            </span>
                          ))}
                    </span>
                  </div>
                  <ChevronDown
                    size={18}
                    className={`audit-group__chevron ${isExpanded ? "audit-group__chevron--open" : ""}`}
                  />
                </div>
                {isExpanded && (
                  <ul className="audit-group__timeline">
                    {group.entries.map((entry) => {
                      const { lead, role } = entryHeadline(entry);
                      const folded = entry.ids.length > 1;
                      return (
                        <li key={entry.ids[0]} className="audit-item">
                          <span
                            className={`audit-item__dot audit-item__dot--${entry.operation.toLowerCase()}`}
                          />
                          <div className="audit-item__body">
                            <div className="audit-item__header">
                              <span
                                className={`audit-entry__op audit-entry__op--${entry.operation.toLowerCase()}`}
                              >
                                {OP_VERB[entry.operation]}
                              </span>
                              {lead ? (
                                <span className="audit-item__lead">{lead}</span>
                              ) : null}
                              {role ? (
                                <span className="audit-item__role">· {role}</span>
                              ) : null}
                              <span className="audit-entry__spacer" />
                              <span
                                className="audit-item__time"
                                title={`${entry.firstAt} – ${entry.lastAt}`}
                              >
                                {formatTimeOnly(entry.lastAt)}
                                {folded ? (
                                  <span className="audit-item__edits">
                                    {entry.ids.length} edits
                                  </span>
                                ) : null}
                              </span>
                            </div>
                            <ChangeList
                              table={entry.table_name}
                              operation={entry.operation}
                              changes={entry.changes}
                            />
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!loading && groups.length > 0 && (
        <div className="audit-log__footer">
          <div className="audit-log__pagination">
            <span className="audit-log__pagination-info">
              {startIndex + 1}–{Math.min(startIndex + PAGE_SIZE, groups.length)} of {groups.length}
            </span>
            <button
              type="button"
              className="board-controls__icon-button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={currentPage <= 1}
              aria-label="Previous page"
            >
              <ChevronLeft size={18} />
            </button>
            <span className="audit-log__pagination-page">
              Page {currentPage} of {totalPages}
            </span>
            <button
              type="button"
              className="board-controls__icon-button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages}
              aria-label="Next page"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default function AuditLog() {
  return (
    <BoardLayout>
      <AuditLogView />
    </BoardLayout>
  );
}
